






import { Component, Provide, Watch, Mixins } from 'vue-property-decorator';
import MixinCharts from '@/plugins/podolib/services/helper/mixins/chart/standard';
import { TChart, TMode } from '@/plugins/podolib/services/helper/mixins/chart';
import { SeriesOptionsType } from 'highcharts';
import { TAcquisition_ComputedData_Standard } from '@/plugins/podolib/services/entities/EntityPatients';

@Component
export default class ChartStancePhaseAverageStepSequence extends Mixins(MixinCharts) implements TChart {
  // Chart name for exportation
  @Provide() readonly name = 'ChartStancePhaseAverageStepSequence';

  // Calculs
  get __digitigrade_left(): TAcquisition_ComputedData_Standard {
    const rsl = this.left.propulsion_time;
    return {
      avg: parseFloat(rsl.avg.toFixed(1)),
      std: parseFloat(rsl.std.toFixed(1)),
    };
  }

  get __digitigrade_right(): TAcquisition_ComputedData_Standard {
    const rsl = this.right.propulsion_time;
    return {
      avg: parseFloat(rsl.avg.toFixed(1)),
      std: parseFloat(rsl.std.toFixed(1)),
    };
  }

  get __plantigrade_left(): TAcquisition_ComputedData_Standard {
    const rsl = this.left.flatfoot_time;
    return {
      avg: parseFloat(rsl.avg.toFixed(1)),
      std: parseFloat(rsl.std.toFixed(1)),
    };
  }

  get __plantigrade_right(): TAcquisition_ComputedData_Standard {
    const rsl = this.right.flatfoot_time;
    return {
      avg: parseFloat(rsl.avg.toFixed(1)),
      std: parseFloat(rsl.std.toFixed(1)),
    };
  }

  get __taligrade_left(): TAcquisition_ComputedData_Standard {
    const rsl = this.left.loading_time;
    return {
      avg: parseFloat(rsl.avg.toFixed(1)),
      std: parseFloat(rsl.std.toFixed(1)),
    };
  }

  get __taligrade_right(): TAcquisition_ComputedData_Standard {
    const rsl = this.right.loading_time;
    return {
      avg: parseFloat(rsl.avg.toFixed(1)),
      std: parseFloat(rsl.std.toFixed(1)),
    };
  }

  get __total_left(): number {
    return this.__digitigrade_left.avg + this.__plantigrade_left.avg + this.__taligrade_left.avg;
  }

  get __total_right(): number {
    return this.__digitigrade_right.avg + this.__plantigrade_right.avg + this.__taligrade_right.avg;
  }

  // Series
  get _val(): SeriesOptionsType[] {
    return [
      {
        name: this.$t('commons.standards.propulsion') as string,
        data: this.isAbsolute
          ? [this.__digitigrade_left.avg, this.__digitigrade_right.avg]
          : [
              this.getPercent('left', this.__digitigrade_left.avg),
              this.getPercent('right', this.__digitigrade_right.avg),
            ],
        color: this.$vuetify.theme.themes.light.chart1 as string,
        index: 0,
        legendIndex: 2,
        type: 'column',
      },
      {
        name: this.$t('commons.standards.flat-foot') as string,
        data: this.isAbsolute
          ? [this.__plantigrade_left.avg, this.__plantigrade_right.avg]
          : [
              this.getPercent('left', this.__plantigrade_left.avg),
              this.getPercent('right', this.__plantigrade_right.avg),
            ],
        color: this.$vuetify.theme.themes.light.chart2 as string,
        index: 1,
        legendIndex: 1,
        type: 'column',
      },
      {
        name: this.$t('commons.sentences.charts.loading') as string,
        data: this.isAbsolute
          ? [this.__taligrade_left.avg, this.__taligrade_right.avg]
          : [this.getPercent('left', this.__taligrade_left.avg), this.getPercent('right', this.__taligrade_right.avg)],
        color: this.$vuetify.theme.themes.light.chart3 as string,
        index: 2,
        legendIndex: 0,
        type: 'column',
      },
    ];
  }

  get series() {
    return [...this._val];
  }

  get isAbsolute(): boolean {
    return this.mode === 'absolute';
  }

  @Watch('mode')
  onSModeChange(newMode: TMode) {
    (this.options as any).yAxis.title.text =
      newMode === 'absolute' ? this.$tc('commons.units.millisecond', 2) : this.$tc('commons.standards.percentage', 1);
  }

  getPercent(side: 'left' | 'right', value: number): number {
    return (value / (side === 'left' ? this.__total_left : this.__total_right)) * 100;
  }

  created() {
    let _this = this;

    this.setOptions({
      chart: {
        type: 'column',
      },
      title: {
        text: !this.comparison
          ? (this.$tc('commons.standards.contact-phase', 2) as string)
          : this.$tc('commons.standards.comparison'),
      },
      xAxis: {
        categories: [this.$tc('commons.standards.left') as string, this.$tc('commons.standards.right') as string],
      },
      yAxis: {
        title: {
          text: String(
            this.isAbsolute ? this.$tc('commons.units.millisecond', 2) : this.$tc('commons.standards.percentage', 1),
          ),
        },
        stackLabels: {
          enabled: true,
          format: '{total:.0f}',
          style: {
            fontWeight: 'bold',
            color: 'gray',
          },
        },
        tooltipValueFormat: '{value:.1f}',
        reversed: false,
      },
      tooltip: {
        formatter() {
          return `<span style="color: ${this.point.color}">${this.series.name}</span> : ${this.y.toFixed(1)} ${
            _this.isAbsolute ? 'ms' : '%'
          }`;
        },
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          pointWidth: 75,
          dataLabels: {
            enabled: true,
            format: '{y:.0f}',
          },
        },
      },
      responsive: {
        rules: [
          {
            condition: {
              minWidth: 10,
            },
            chartOptions: {
              yAxis: {
                min: 0,
                max: null,
              },
            },
          },
        ],
      },
      series: [],
    });
    this.setSeries(this.series);
  }
}
